import { useQuery, useMutation } from "@1f/react-sdk";
import sharedDatasInstance from "../sharedDatas";
import { GET_USER_ROLES, GET_USER_ROLE_BY_ID, GET_USER_ROLES_DETAILS } from "../query/userRolesQueries";
import { POST_USER_ROLE, UPDATE_USER_ROLE, DELETE_USER_ROLE } from "../mutations/userRolesMutations";
import { apiErrorHelper } from "../helpers/apiErrorHelpers";

export const UserRolesApi = () => {
	const tenantId = sharedDatasInstance.getPropertyByName("tenantId");
	const loggedUserId = sharedDatasInstance.getPropertyByName("userId");
	const aziendaId = sharedDatasInstance.getPropertyByName("aziendaId");
    const aziendaName = sharedDatasInstance.getPropertyByName("aziendaName");
	const errorApiHelpers = apiErrorHelper();
	const apiGetUserRoles = useQuery(
		GET_USER_ROLES,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);
	const apiGetUserRoleById = useQuery(
		GET_USER_ROLE_BY_ID,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);
	const apiGetUserRolesDetails = useQuery(
		GET_USER_ROLES_DETAILS,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);
	const apiPostUserRole = useMutation(
		POST_USER_ROLE,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);
	const apiUpdateUserRole = useMutation(
		UPDATE_USER_ROLE,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);
	const apiDeleteUserRole = useMutation(
		DELETE_USER_ROLE,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" }
	);

	/**
	 * Get all user roles for the current tenant and azienda
	 * @returns {Promise<Array>} Array of user roles
	 */
	const GetUserRoles = async () => {
		try {
			const response = await apiGetUserRoles.fetch({
				tenantId: tenantId,
				aziendaId: aziendaId
			});
			if (!response.data.errors) {
				return response.data.data.getUserRoles;
			}
		} catch (error) {
			throw new Error("Impossibile recuperare i ruoli utente");
		}
	};
	/**
	 * Get a specific user role by ID
	 * @param {number} roleId - The ID of the user role
	 * @returns {Promise<Object>} The user role object
	 */
	const GetUserRoleById = async (roleId) => {
		try {
			const response = await apiGetUserRoleById.fetch({
				id: roleId,
				userId: loggedUserId,
				tenantId: tenantId,
				aziendaId: aziendaId
			});
			if (!response.data.errors) {
				return response.data.data.getUserRoleById;
			}
		} catch (error) {
			throw new Error("Impossibile recuperare il ruolo utente");
		}
	};
	/**
	 * Get user roles details for a specific user
	 * @param {string} targetUserId - The ID of the target user
	 * @returns {Promise<Array>} Array of user roles for the target user
	 */
	const GetUserRolesDetails = async (targetUserId) => {
		try {
			const response = await apiGetUserRolesDetails.fetch({
				tenantId: tenantId,
				aziendaId: aziendaId,
				userId: loggedUserId,
				targetUserId: targetUserId
			});
			if (!response.data.errors) {
				return response.data.data.getUserRolesDetails;
			}
		} catch (error) {
			throw new Error("Impossibile recuperare i dettagli dei ruoli utente");
		}
	};
	/**
	 * Create a new user role
	 * @param {Object} userRoleData - The user role data to create
	 * @returns {Promise<Object>} The created user role
	 */
	const PostUserRole = async (userRoleData) => {
        try {
            const response = await apiPostUserRole.fetch({
                tenantId: tenantId,
                aziendaId: aziendaId,
                dataInput: userRoleData
            });
            if (!response.data.errors) {
                return response.data.data.postUserRole;
            }
        } catch (error) {
            if(error.message.toLowerCase().includes('already has an active user with role')){
                throw new Error(`Il soggetto obbligato ${aziendaName} ha già un utente con questo ruolo attivo`);
            }
            let errorMessage = errorApiHelpers.handleApiError(error);
            throw new Error(errorMessage || "Impossibile creare il ruolo utente");
        }
	};
	/**
	 * Update an existing user role
	 * @param {number} roleId - The ID of the user role to update
	 * @param {Object} userRoleData - The updated user role data
	 * @returns {Promise<Object>} The updated user role
	 */
	const UpdateUserRole = async (roleId, userRoleData) => {
		try {
			const response = await apiUpdateUserRole.fetch({
				id: roleId,
				userId: loggedUserId,
				tenantId: tenantId,
				aziendaId: aziendaId,
				dataInput: userRoleData
			});
			if (!response.data.errors) {
				return response.data.data.updateUserRole;
			}
		} catch (error) {
            if(error.message.toLowerCase().includes('already has an active user with role')){
				throw new Error(`Il soggetto obbligato ${aziendaName} ha già un utente con questo ruolo attivo`);
            }
			let errorMessage = errorApiHelpers.handleApiError(error);
			throw new Error(errorMessage || "Impossibile aggiornare il ruolo utente");
		}
	};
	/**
	 * Delete a user role
	 * @param {number} roleId - The ID of the user role to delete
	 * @returns {Promise<Object>} The delete response with success and message
	 */
	const DeleteUserRole = async (roleId) => {
		try {
			const response = await apiDeleteUserRole.fetch({
				id: roleId,
				userId: loggedUserId,
				tenantId: tenantId,
				aziendaId: aziendaId
			});
			if (!response.data.errors) {
				return response.data.data.deleteUserRole;
			}
		} catch (error) {
			let errorMessage = errorApiHelpers.handleApiError(error);
			throw new Error(errorMessage || "Impossibile eliminare il ruolo utente");
		}
	};

	return {
		GetUserRoles,
		GetUserRoleById,
		GetUserRolesDetails,
		PostUserRole,
		UpdateUserRole,
		DeleteUserRole
	};
};
